import React, { Suspense } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from '@/i18n';
import { useThemeStore, useAuthStore } from '@/stores';
import { isAuthenticated, refreshAccessToken, isTokenExpired, isTokenExpiringSoon } from '@/utils/auth';
import { queryClient } from '@/services/query-client';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { apiClient } from '@/services/api-client';
import { supabase } from '@/utils/supabase';
import { UpdateNotifier } from '@/components/common/UpdateNotifier';

// Intercept Supabase OAuth redirection hash parameters to prevent HashRouter 404
if (typeof window !== 'undefined' && window.location.hash.includes('access_token=')) {
  console.log('Intercepting Supabase OAuth redirect hash to avoid HashRouter 404');
  window.location.hash = '#/login';
}

// Layouts
import MainLayout from '@/layouts/MainLayout';

// Pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import CustomersPage from '@/pages/CustomersPage';
import SubscriptionsPage from '@/pages/SubscriptionsPage';
import RequestsPage from '@/pages/RequestsPage';
import TasksPage from '@/pages/TasksPage';
import WalletPage from '@/pages/WalletPage';
import LocationsPage from '@/pages/LocationsPage';
import SettingsPage from '@/pages/SettingsPage';
import AlertsPage from '@/pages/AlertsPage';
import WhatsAppPairingPage from '@/pages/WhatsAppPairingPage';
import NotFoundPage from '@/pages/NotFoundPage';
import ServicesPlansPage from '@/pages/ServicesPlansPage';
import TicketsPage from '@/pages/TicketsPage';
import SessionsPage from '@/pages/SessionsPage';
import SubscriberSessionsPage from '@/pages/SubscriberSessionsPage';
import DebtsPage from '@/pages/DebtsPage';
import NotificationCenterPage from '@/pages/NotificationCenterPage';
import UsersPermissionsPage from '@/pages/UsersPermissionsPage';
import PowerMonitoringPage from '@/pages/PowerMonitoringPage';


import { usePermission, CanonicalPermission } from '@/hooks/usePermission';
import AccessDenied from '@/components/common/AccessDenied';

// Loading component
const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
);

// Protected Route Component (Asynchronous auth & permission verification)
export const ProtectedRoute: React.FC<{
  element?: React.ReactElement;
  children?: React.ReactNode;
  requiredPermission?: CanonicalPermission | CanonicalPermission[];
  requireAll?: boolean;
}> = ({ element, children, requiredPermission, requireAll = false }) => {
  const storeToken = useAuthStore((state) => state.accessToken);
  const accessToken = storeToken ?? (typeof useAuthStore.getState === 'function' ? useAuthStore.getState().accessToken : null);
  const storeUser = useAuthStore((state) => state.user);
  const user = storeUser ?? (typeof useAuthStore.getState === 'function' ? useAuthStore.getState().user : null);
  const { hasPermission, hasAllPermissions, hasAnyPermission } = usePermission();
  
  // Synchronously check if we are already authenticated with a non-expired token
  const isCurrentlyValid = Boolean(accessToken && !isTokenExpired(accessToken));
  
  const [isChecking, setIsChecking] = React.useState(!isCurrentlyValid);
  const [isValid, setIsValid] = React.useState(!!isCurrentlyValid);

  React.useEffect(() => {
    const checkAuth = async () => {
      if (!accessToken) {
        setIsValid(false);
        setIsChecking(false);
        return;
      }

      if (isTokenExpired(accessToken)) {
        console.log('ProtectedRoute: Token expired, attempting refresh...');
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          console.log('ProtectedRoute: Token refreshed successfully.');
          setIsValid(true);
          setIsChecking(false);
        } else {
          console.log('ProtectedRoute: Token refresh failed, requiring login...');
          setIsValid(false);
          setIsChecking(false);
        }
      } else {
        setIsValid(true);
        setIsChecking(false);
      }
    };

    if (!isCurrentlyValid) {
      checkAuth();
    } else {
      setIsValid(true);
      setIsChecking(false);
    }
  }, [accessToken, isCurrentlyValid]);

  React.useEffect(() => {
    if (!isChecking && !isValid) {
      window.location.hash = '#/login';
    }
  }, [isChecking, isValid]);

  if (isChecking) {
    return <LoadingSpinner />;
  }

  if (!isValid) {
    return null;
  }

  if (user?.forcePasswordChange) {
    window.location.hash = '#/login';
    return null;
  }

  if (requiredPermission) {
    let isAllowed = false;
    if (Array.isArray(requiredPermission)) {
      isAllowed = requireAll
        ? hasAllPermissions(requiredPermission)
        : hasAnyPermission(requiredPermission);
    } else {
      isAllowed = hasPermission(requiredPermission);
    }

    if (!isAllowed) {
      const permKey = Array.isArray(requiredPermission) ? requiredPermission.join(', ') : requiredPermission;
      return <AccessDenied requiredPermission={permKey} />;
    }
  }

  const content = element || children;
  return <>{content}</>;
};

function App() {
  const { theme, language } = useThemeStore();
  const accessToken = useAuthStore((state) => state.accessToken);
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true);

  React.useEffect(() => {
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  React.useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.documentElement.setAttribute('data-theme', 'dark');
      document.body.classList.remove('light-mode');
      document.body.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark');
      document.documentElement.setAttribute('data-theme', 'light');
      document.body.classList.remove('dark-mode');
      document.body.classList.add('light-mode');
    }
  }, [theme]);

  React.useEffect(() => {
    const initializeAuth = async () => {
      setIsCheckingAuth(false);
    };

    initializeAuth();
  }, []);

  React.useEffect(() => {
    if (accessToken) {
      const OneSignalDeferred = (window as any).OneSignalDeferred || [];
      OneSignalDeferred.push(async (OneSignal: any) => {
        try {
          // If on a mobile device, automatically opt-in and request notification permission
          const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
          if (isMobile) {
            console.log('[App] Mobile device detected. Auto-subscribing to OneSignal Push...');
            localStorage.setItem('notification_desktop_enabled', 'true');
            
            const sub = OneSignal.User?.pushSubscription;
            if (sub) {
              await sub.optIn();
            }
            if (OneSignal.Notifications?.requestPermission) {
              await OneSignal.Notifications.requestPermission();
            }
          }

          const register = async () => {
            const subId = OneSignal.User?.pushSubscription?.id;
            const optedIn = OneSignal.User?.pushSubscription?.optedIn;

            // Self-healing: if browser permission is already granted but OneSignal optedIn is false, force optIn
            if (window.Notification?.permission === 'granted' && !optedIn) {
              console.log('[App] Permission is granted but OneSignal optedIn is false. Forcing optIn...');
              await OneSignal.User?.pushSubscription?.optIn();
              return; // The 'change' event listener below will handle the registration once it is done
            }

            if (subId && optedIn) {
              const isMob = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
              await apiClient.post('/notifications/register-device', {
                subscriptionId: subId,
                device_type: isMob ? 'mobile' : 'desktop',
                platform: typeof navigator !== 'undefined' ? navigator.platform : undefined,
                browser: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
              });
              console.log('[App] OneSignal device registered successfully:', subId);
            }
          };

          // Register initially
          await register();

          // Listen for change events
          OneSignal.User?.pushSubscription?.addEventListener('change', async () => {
            await register();
          });
        } catch (err) {
          console.warn('[App] Failed to auto-register OneSignal subscription:', err);
        }
      });
    }
  }, [accessToken]);

  if (isCheckingAuth) {
    return <LoadingSpinner />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <UpdateNotifier />
      <I18nextProvider i18n={i18n}>
        <Router>
          <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/whatsapp-pairing"
                  element={<ProtectedRoute element={<WhatsAppPairingPage />} requiredPermission="settings.manage" />}
                />

                {/* Protected Routes */}
                <Route element={<MainLayout />}>
                  <Route
                    path="/"
                    element={<ProtectedRoute element={<DashboardPage />} />}
                  />
                  <Route
                    path="/customers"
                    element={<ProtectedRoute element={<CustomersPage />} requiredPermission="customers.read" />}
                  />
                  <Route
                    path="/subscriptions"
                    element={<ProtectedRoute element={<SubscriptionsPage />} requiredPermission="subscriptions.read" />}
                  />
                  <Route
                    path="/services-plans"
                    element={<ProtectedRoute element={<ServicesPlansPage />} requiredPermission="subscriptions.read" />}
                  />
                  <Route
                    path="/requests"
                    element={<ProtectedRoute element={<RequestsPage />} requiredPermission="requests.read" />}
                  />
                  <Route
                    path="/tasks"
                    element={<ProtectedRoute element={<TasksPage />} requiredPermission="tasks.read" />}
                  />
                  <Route
                    path="/wallet"
                    element={<ProtectedRoute element={<WalletPage />} requiredPermission="wallet.read" />}
                  />
                  <Route
                    path="/tickets"
                    element={<ProtectedRoute element={<TicketsPage />} requiredPermission="tickets.read" />}
                  />
                  <Route
                    path="/subscriber-sessions"
                    element={<ProtectedRoute element={<SubscriberSessionsPage />} requiredPermission="customers.read" />}
                  />
                  <Route
                    path="/sessions"
                    element={<ProtectedRoute element={<SessionsPage />} requiredPermission="customers.read" />}
                  />
                  <Route
                    path="/locations"
                    element={<ProtectedRoute element={<LocationsPage />} requiredPermission="customers.read" />}
                  />
                  <Route
                    path="/debts"
                    element={<ProtectedRoute element={<DebtsPage />} requiredPermission="debts.manage" />}
                  />
                  <Route
                    path="/power-monitor"
                    element={<ProtectedRoute element={<PowerMonitoringPage />} requiredPermission="customers.read" />}
                  />
                  <Route
                    path="/settings"
                    element={<ProtectedRoute element={<SettingsPage />} requiredPermission="settings.read" />}
                  />
                  <Route
                    path="/settings/users-permissions"
                    element={<ProtectedRoute element={<UsersPermissionsPage />} requiredPermission="users.manage" />}
                  />
                  <Route
                    path="/users-permissions"
                    element={<ProtectedRoute element={<UsersPermissionsPage />} requiredPermission="users.manage" />}
                  />
                  <Route
                    path="/alerts"
                    element={<ProtectedRoute element={<AlertsPage />} requiredPermission="settings.read" />}
                  />
                  <Route
                    path="/notifications"
                    element={<ProtectedRoute element={<NotificationCenterPage />} />}
                  />
                </Route>

                {/* 404 Page */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </ErrorBoundary>
        </Router>
      </I18nextProvider>
    </QueryClientProvider>
  );
}

export default App;
